<template>
  <modal :show.sync="showModal" class="dark-modal user-form-modal">
    <div class="modal-box">
      <div class="modal-box-header">
        <h2 class="modal-title">{{ isEditMode ? $t('users.updateUser', { username: formData.name }) : $t('users.addUser') }}</h2>
      </div>

      <form @submit.prevent="handleSubmit">
        <!-- Email Field -->
        <div class="field">
          <label class="label">
            {{ $t('email') }}<span class="required-asterisk"> *</span>
            <popover v-if="!isEditMode" placement="top" width="300px">
              <span slot="trigger" class="icon is-small has-text-info" style="cursor: help;">
                <octicon :icon="info" />
              </span>
              <div class="has-text-left is-size-7">
                {{ $t('users.tooltips.email') }}
              </div>
            </popover>
          </label>
          <div class="control">
            <input
              v-model="formData.email"
              class="input"
              type="text"
              :class="{ 'is-danger': validationErrors.email }"
              :disabled="isEditMode"
              :placeholder="$t('users.placeholders.email')"
              autocomplete="username"
              required
              @blur="handleEmailBlur"
              @input="handleEmailInput"
            >
          </div>
          <p v-if="validationErrors.email" class="help is-danger">
            {{ validationErrors.email }}
          </p>
        </div>

        <!-- Username Field -->
        <div class="field">
          <label class="label">
            {{ $t('users.username') }}<span class="required-asterisk"> *</span>
            <popover v-if="!isEditMode" placement="top" width="300px">
              <span slot="trigger" class="icon is-small has-text-info" style="cursor: help;">
                <octicon :icon="info" />
              </span>
              <div class="has-text-left is-size-7">
                {{ $t('users.tooltips.username') }}
              </div>
            </popover>
          </label>
          <div class="control">
            <input
              v-model="formData.name"
              class="input"
              type="text"
              :class="{ 'is-danger': validationErrors.name }"
              :disabled="isEditMode"
              :placeholder="$t('users.placeholders.username')"
              required
              autocomplete="name"
              @blur="handleUsernameBlur"
              @input="handleUsernameInput"
            >
          </div>
          <p v-if="validationErrors.name" class="help is-danger">
            {{ validationErrors.name }}
          </p>
        </div>

        <hr v-if="!isEditMode">

        <!-- Password Field -->
        <div v-if="!isEditMode" class="field">
          <label class="label">
            {{ $t('password') }}<span class="required-asterisk"> *</span>
            <popover placement="top" width="300px">
              <span slot="trigger" class="icon is-small has-text-info" style="cursor: help;">
                <octicon :icon="info" />
              </span>
              <div class="has-text-left is-size-7">
                {{ $t('users.tooltips.password') }}
              </div>
            </popover>
          </label>
          <div class="control has-icons-right">
            <input
              v-model="formData.password"
              class="input"
              :type="showPassword ? 'text' : 'password'"
              :class="{ 'is-danger': validationErrors.password }"
              :placeholder="$t('users.placeholders.password')"
              autocomplete="new-password"
              :required="!isEditMode"
              @blur="handlePasswordBlur"
              @input="handlePasswordInput"
            >
            <span
              class="icon is-small is-right"
              style="cursor: pointer; pointer-events: auto;"
              @click="togglePasswordVisibility"
              :title="showPassword ? $t('users.hidePassword') : $t('users.showPassword')"
            >
              <octicon :icon="showPassword ? eyeClosed : eye" />
            </span>
          </div>
          <p v-if="validationErrors.password" class="help is-danger">
            {{ validationErrors.password }}
          </p>
        </div>

        <!-- Confirm Password Field -->
        <div v-if="!isEditMode" class="field">
          <label class="label">
            {{ $t('users.confirmPassword') }}<span class="required-asterisk"> *</span>
            <popover placement="top" width="300px">
              <span slot="trigger" class="icon is-small has-text-info" style="cursor: help;">
                <octicon :icon="info" />
              </span>
              <div class="has-text-left is-size-7">
                {{ $t('users.tooltips.confirmPassword') }}
              </div>
            </popover>
          </label>
          <div class="control has-icons-right">
            <input
              v-model="formData.confirmPassword"
              class="input"
              :type="showConfirmPassword ? 'text' : 'password'"
              :class="{ 'is-danger': validationErrors.confirmPassword }"
              :placeholder="$t('users.placeholders.confirmPassword')"
              autocomplete="new-password"
              required
              @blur="handleConfirmPasswordBlur"
              @input="handleConfirmPasswordInput"
            >
            <span
              class="icon is-small is-right"
              style="cursor: pointer; pointer-events: auto;"
              @click="toggleConfirmPasswordVisibility"
              :title="showConfirmPassword ? $t('users.hidePassword') : $t('users.showPassword')"
            >
              <octicon :icon="showConfirmPassword ? eyeClosed : eye" />
            </span>
          </div>
          <p v-if="validationErrors.confirmPassword" class="help is-danger">
            {{ validationErrors.confirmPassword }}
          </p>
        </div>

        <hr v-if="!isEditMode">

        <!-- Scope Selection -->
        <div class="field">
          <label class="label">{{ $t('role') }}</label>
          <div class="control">
            <div class="select is-fullwidth">
              <select v-model="formData.scope" required>
                <option value="root">{{ $t('users.roles.root') }}</option>
                <option value="admin">{{ $t('users.roles.admin') }}</option>
                <option value="user">{{ $t('users.roles.user') }}</option>
                <option value="monitor">{{ $t('users.roles.monitor') }}</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Form Actions -->
        <div class="modal-footer">
          <button type="button" class="button cancel-button" @click.stop.prevent="handleCancel" :disabled="isValidating">
            {{ $t('cancel') }}
          </button>
          <button
            type="submit"
            class="button is-primary"
            :class="{ 'is-loading': isValidating }"
            :disabled="isValidating"
          >
            {{ isEditMode ? $t('users.filter.confirm') : $t('save') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Cancel Confirmation Modal -->
    <modal :show.sync="showCancelConfirm" class="cancel-confirm-modal">
      <div class="confirm-box">
        <div class="confirm-header">
          <h3>{{ $t('indexes.cancelTitle') }}</h3>
        </div>
        <div class="confirm-body">
          <p>{{ $t('indexes.cancelMessage') }}</p>
        </div>
        <div class="confirm-footer">
          <button
            type="button"
            class="button cancel-button"
            @click="showCancelConfirm = false"
          >
            {{ $t('cancel') }}
          </button>
          <button
            type="button"
            class="button is-primary"
            @click="confirmCancel"
          >
            {{ $t('confirm') }}
          </button>
        </div>
      </div>
    </modal>
  </modal>
</template>

<script>
import { Modal, Popover } from '@cyradar/ui'
import { play, stop, info, sync, clippy, eye, eyeClosed } from 'octicons-vue'
import { validatePasswordFormat, validatePasswordConfirmation, validateEmailFormat, validateUsernameFormat, generateValidPassword } from '@/utils/passwordValidation'
import clipboardCopy from '@/clipboard'
export default {
  name: 'UserForm',
  components: {
    Modal,
    Popover
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    user: {
      type: Object,
      default: null
    },
    currentUser: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      formData: {
        id: '',
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        scope: 'admin', // Default to admin
        status: 'active'
      },
      validationErrors: {
        email: '',
        name: '',
        password: '',
        confirmPassword: ''
      },
      isValidating: false,
      showPassword: false,
      showConfirmPassword: false,
      allowClose: false,
      showCancelConfirm: false
    }
  },
  computed: {
    showModal: {
      get() {
        return this.show
      },
      set(value) {
        // Only allow closing the modal through explicit user actions (close button, cancel button)
        // Prevent accidental closure from clicking outside the modal
        if (!value && !this.allowClose) {
          return
        }
        this.$emit('update:show', value)
      }
    },
    play() {
      return play
    },
    stop() {
      return stop
    },
    info() {
      return info
    },
    sync() {
      return sync
    },
    clippy() {
      return clippy
    },
    eye() {
      return eye
    },
    eyeClosed() {
      return eyeClosed
    },
    isEditMode() {
      return !!this.formData.id
    },
    isFormValid() {
      return !this.validationErrors.email &&
             !this.validationErrors.name &&
             !this.validationErrors.password &&
             !this.validationErrors.confirmPassword &&
             this.formData.email &&
             this.formData.name &&
             (this.isEditMode || (this.formData.password && this.formData.confirmPassword))
    }
  },
  watch: {
    user: {
      handler(newUser) {
        // Prevent unnecessary form resets during language changes or re-renders
        // Only update form if the user actually changed (by comparing IDs)
        const newUserId = newUser ? newUser.id : null
        const currentFormId = this.formData.id

        // If user prop changed to a different user, initialize form with new user data
        if (newUser && newUserId !== currentFormId) {
          this.initializeForm(newUser)
        }
        // If user prop changed from a user to null, reset form only if we currently have a user loaded
        else if (!newUser && currentFormId) {
          this.resetForm()
        }
        // Otherwise, do nothing - this prevents form reset during language changes
      },
      immediate: true
    },
    show: {
      handler(newValue, oldValue) {
        // When modal is opened for creating new user, ensure form is reset
        // Only reset if transitioning from closed to open (not during re-renders)
        if (newValue && !oldValue && !this.user) {
          this.resetForm()
        }
      },
      immediate: false
    }
  },
  methods: {
    /**
     * Validates email format and uniqueness using shared utility
     */
    async validateEmail() {
      // First validate format using shared utility
      const formatResult = validateEmailFormat(this.formData.email, (key, params) => this.$t(key, params))

      if (!formatResult.isValid) {
        this.validationErrors.email = formatResult.error
        return false
      }

      // Check email uniqueness (only for new users or when email changed)
      const shouldCheckUniqueness = !this.isEditMode || (this.user && this.formData.email !== this.user.email)

      if (shouldCheckUniqueness) {
        try {
          const response = await this.$http.get(`/api/v1/users?email=${encodeURIComponent(this.formData.email)}`)

          if (response && response.data && response.data.data && response.data.data.items.length > 0) {
            this.validationErrors.email = this.$t('users.validation.emailExists')
            return false
          }
        } catch (error) {
          // If API call fails, we'll let server-side validation handle it
          console.warn('Email uniqueness check failed:', error)
        }
      }

      this.validationErrors.email = ''
      return true
    },

    /**
     * Validates username format and uniqueness using shared utility
     */
    async validateUsername() {
      // First validate format using shared utility
      const formatResult = validateUsernameFormat(this.formData.name, (key, params) => this.$t(key, params))

      if (!formatResult.isValid) {
        this.validationErrors.name = formatResult.error
        return false
      }

      // Check username uniqueness (case-insensitive, only for new users or when name changed)
      const shouldCheckUniqueness = !this.isEditMode || (this.user && this.formData.name.toLowerCase() !== this.user.name.toLowerCase())

      if (shouldCheckUniqueness) {
        try {
          const response = await this.$http.get(`/api/v1/users?name=${encodeURIComponent(this.formData.name)}`)

          if (response && response.data && response.data.data && response.data.data.items.length > 0) {
            // Check if any existing user has the same name (case-insensitive)
            const existingUser = response.data.data.items.find(user =>
              user.name.toLowerCase() === this.formData.name.toLowerCase()
            )

            if (existingUser) {
              this.validationErrors.name = this.$t('users.validation.usernameExists')
              return false
            }
          }
        } catch (error) {
          // If API call fails, we'll let server-side validation handle it
          console.warn('Username uniqueness check failed:', error)
        }
      }

      this.validationErrors.name = ''
      return true
    },

    /**
     * Validates password format using shared utility
     */
    validatePassword() {
      // Password is only required for new users
      if (!this.isEditMode && !this.formData.password) {
        this.validationErrors.password = this.$t('users.validation.passwordRequired')
        return false
      }

      // If password is provided, validate format using shared utility
      if (this.formData.password) {
        const result = validatePasswordFormat(this.formData.password, (key, params) => this.$t(key, params))

        this.validationErrors.password = result.error
        return result.isValid
      }

      this.validationErrors.password = ''
      return true
    },

    /**
     * Validates password confirmation using shared utility
     */
    validatePasswordConfirmation() {
      // Only validate for new users
      if (this.isEditMode) {
        return true
      }

      const result = validatePasswordConfirmation(this.formData.password, this.formData.confirmPassword, (key, params) => this.$t(key, params))

      this.validationErrors.confirmPassword = result.error
      return result.isValid
    },

    /**
     * Clears validation error for a specific field
     */
    clearFieldError(field) {
      this.validationErrors[field] = ''
    },

    /**
     * Validates all form fields
     */
    async validateForm() {
      this.isValidating = true

      const emailValid = await this.validateEmail()
      const usernameValid = await this.validateUsername()
      const passwordValid = this.validatePassword()
      const confirmPasswordValid = this.validatePasswordConfirmation()

      this.isValidating = false

      return emailValid && usernameValid && passwordValid && confirmPasswordValid
    },

    /**
     * Initializes form with user data for editing
     */
    initializeForm(user) {
      const userScope = Array.isArray(user.scopes) && user.scopes.length > 0 ? user.scopes[0] : ''

      this.formData = {
        id: user.id || '',
        name: user.name || '',
        email: user.email || '',
        scope: userScope,
        status: user.status || 'active',
        password: '',
      }
    },
    /**
     * Resets form to default values and clears all validation errors
     * Ensures clean state for new form instances
     */
    resetForm() {
      // Reset all form data to initial state
      this.formData = {
        id: '',
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        scope: 'admin', // Default to admin
        status: 'active'
      }

      // Clear all validation errors
      this.validationErrors = {
        email: '',
        name: '',
        password: '',
        confirmPassword: ''
      }

      // Reset UI state
      this.showPassword = false
      this.showConfirmPassword = false
      this.isValidating = false
    },
    /**
     * Toggles user status between active and inactive
     */
    toggleStatus() {
      this.formData.status = this.formData.status === 'active' ? 'inactive' : 'active'
    },
    /**
     * Handles form submission
     */
    async handleSubmit() {
      // Validate form before submission
      const isValid = await this.validateForm()

      if (!isValid) {
        this.$store.dispatch('NOTIFY', {
          type: 'warning',
          text: this.$t('users.validation.formHasErrors')
        })
        return
      }

      if (this.formData.id) {
        this.updateUser()
      } else {
        this.createUser()
      }
    },
    /**
     * Creates a new user
     */
    createUser() {
      const payload = {
        name: this.formData.name,
        email: this.formData.email,
        password: this.formData.password,
        scopes: this.formData.scope ? [this.formData.scope] : [],
        status: this.formData.status
      }

      return this.$http.post('/api/v1/users', payload).then(body => {
        if (!body || !body.data) {
          return
        }

        this.allowClose = true
        this.resetForm()
        this.$emit('update:show', false)
        this.$nextTick(() => {
          this.allowClose = false
        })

        this.$store.dispatch('NOTIFY', {
          type: 'success',
          text: body.data.message
        })

        this.$emit('user-saved')
      }).catch(error => {
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: error.response?.data?.message || this.$t('users.userCreateError')
        })
      })
    },
    /**
     * Updates an existing user
     */
    updateUser() {
      const payload = {
        name: this.formData.name,
        email: this.formData.email,
        scopes: this.formData.scope ? [this.formData.scope] : [],
        status: this.formData.status
      }

      // Include password only if provided
      if (this.formData.password && this.formData.password.trim()) {
        payload.password = this.formData.password
      }

      return this.$http.patch(`/api/v1/users/${this.formData.id}`, payload).then(body => {
        if (!body || !body.data) {
          return
        }

        this.allowClose = true
        this.resetForm()
        this.$emit('update:show', false)
        this.$nextTick(() => {
          this.allowClose = false
        })

        this.$store.dispatch('NOTIFY', {
          type: 'success',
          text: body.data.message
        })

        this.$emit('user-saved')
      }).catch(error => {
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: error.response?.data?.message || this.$t('users.userUpdateError')
        })
      })
    },
    /**
     * Handles cancel button click - shows confirmation dialog
     */
    handleCancel() {
      this.showCancelConfirm = true
    },

    /**
     * Confirms cancellation and closes the modal
     */
    confirmCancel() {
      this.showCancelConfirm = false
      this.handleClose()
    },

    /**
     * Handles modal close - clears form data and allows modal to close
     */
    handleClose() {
      this.allowClose = true
      this.resetForm()
      this.$emit('update:show', false)
      // Reset allowClose flag after a short delay to prevent immediate reopening issues
      this.$nextTick(() => {
        this.allowClose = false
      })
    },

    /**
     * Generates a random password that meets all validation requirements
     */
    generatePassword() {
      const newPassword = generateValidPassword()
      this.formData.password = newPassword
      this.formData.confirmPassword = newPassword

      // Clear any existing password validation errors
      this.clearFieldError('password')
      this.clearFieldError('confirmPassword')

      // Show success notification
      this.$store.dispatch('NOTIFY', {
        type: 'success',
        text: this.$t('users.passwordGenerated')
      })
    },

    /**
     * Copies the current password to clipboard
     */
    async copyPasswordToClipboard() {
      if (!this.formData.password) {
        this.$store.dispatch('NOTIFY', {
          type: 'warning',
          text: this.$t('users.passwordCopyError')
        })
        return
      }

      try {
        await clipboardCopy(this.formData.password)
        this.$store.dispatch('NOTIFY', {
          type: 'success',
          text: this.$t('users.passwordCopiedSuccess')
        })
      } catch (error) {
        console.error('Failed to copy password:', error)
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: this.$t('users.passwordCopyError')
        })
      }
    },

    /**
     * Toggles visibility of the password field
     */
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword
    },

    /**
     * Toggles visibility of the confirm password field
     */
    toggleConfirmPasswordVisibility() {
      this.showConfirmPassword = !this.showConfirmPassword
    },

    // Event handlers for form fields
    handleEmailBlur() {
      this.validateEmail()
    },

    handleEmailInput() {
      // Automatically trim email to 1000 characters if exceeded
      if (this.formData.email && this.formData.email.length > 1000) {
        this.formData.email = this.formData.email.substring(0, 1000)
      }
      this.clearFieldError('email')
    },

    handleUsernameBlur() {
      this.validateUsername()
    },

    handleUsernameInput() {
      this.clearFieldError('name')
    },

    handlePasswordBlur() {
      this.validatePassword()
    },

    handlePasswordInput() {
      // Check if input contains Vietnamese characters
      const hasNonAscii = /[^\x00-\x7F]/.test(this.formData.password)

      if (hasNonAscii) {
        // Remove Vietnamese characters and show error message
        this.formData.password = this.formData.password.replace(/[^\x00-\x7F]/g, '')
        this.validationErrors.password = this.$t('users.validation.passwordAsciiOnly')
      } else {
        this.clearFieldError('password')
      }

      // Limit to 60 characters
      const cleaned = this.formData.password.substring(0, 60)
      if (this.formData.password !== cleaned) {
        this.formData.password = cleaned
      }

      // Re-validate confirm password if it has a value
      if (this.formData.confirmPassword) {
        this.validatePasswordConfirmation()
      }
    },

    handlePasswordPaste(event) {
      // Prevent default paste behavior
      event.preventDefault()

      // Get pasted text
      const pastedText = (event.clipboardData || window.clipboardData).getData('text')

      // Check if pasted text contains Vietnamese characters
      const hasNonAscii = /[^\x00-\x7F]/.test(pastedText)

      if (hasNonAscii) {
        // Remove Vietnamese characters and show error message
        const cleaned = pastedText.replace(/[^\x00-\x7F]/g, '').substring(0, 60)
        this.formData.password = cleaned
        this.validationErrors.password = this.$t('users.validation.passwordAsciiOnly')
      } else {
        // Paste and limit to 60 characters
        const cleaned = pastedText.substring(0, 60)
        this.formData.password = cleaned
        this.clearFieldError('password')
      }

      // Re-validate confirm password if it has a value
      if (this.formData.confirmPassword) {
        this.validatePasswordConfirmation()
      }
    },

    handleConfirmPasswordBlur() {
      this.validatePasswordConfirmation()
    },

    handleConfirmPasswordInput() {
      // Check if input contains Vietnamese characters
      const hasNonAscii = /[^\x00-\x7F]/.test(this.formData.confirmPassword)

      if (hasNonAscii) {
        // Remove Vietnamese characters and show error message
        this.formData.confirmPassword = this.formData.confirmPassword.replace(/[^\x00-\x7F]/g, '')
        this.validationErrors.confirmPassword = this.$t('users.validation.passwordAsciiOnly')
      } else {
        this.clearFieldError('confirmPassword')
      }

      // Limit to 60 characters
      const cleaned = this.formData.confirmPassword.substring(0, 60)
      if (this.formData.confirmPassword !== cleaned) {
        this.formData.confirmPassword = cleaned
      }

      // Re-validate password confirmation
      if (this.formData.password) {
        this.validatePasswordConfirmation()
      }
    },

    handleConfirmPasswordPaste(event) {
      // Prevent default paste behavior
      event.preventDefault()

      // Get pasted text
      const pastedText = (event.clipboardData || window.clipboardData).getData('text')

      // Check if pasted text contains Vietnamese characters
      const hasNonAscii = /[^\x00-\x7F]/.test(pastedText)

      if (hasNonAscii) {
        // Remove Vietnamese characters and show error message
        const cleaned = pastedText.replace(/[^\x00-\x7F]/g, '').substring(0, 60)
        this.formData.confirmPassword = cleaned
        this.validationErrors.confirmPassword = this.$t('users.validation.passwordAsciiOnly')
      } else {
        // Paste and limit to 60 characters
        const cleaned = pastedText.substring(0, 60)
        this.formData.confirmPassword = cleaned
        this.clearFieldError('confirmPassword')
      }

      // Re-validate password confirmation
      if (this.formData.password) {
        this.validatePasswordConfirmation()
      }
    }
  }
}
</script>

<style lang="scss">
// Cancel Confirmation Modal Styles
.cancel-confirm-modal {
  .confirm-box {
    background-color: #1a1d2e;
    border-radius: 8px;
    padding: 2rem;
    max-width: 500px;

    .confirm-header {
      margin-bottom: 1.5rem;

      h3 {
        color: #fff;
        font-size: 1.25rem;
        font-weight: 600;
        margin: 0;
      }
    }

    .confirm-body {
      margin-bottom: 2rem;

      p {
        color: rgba(255, 255, 255, 0.8);
        font-size: 1rem;
        line-height: 1.5;
        margin: 0;
      }
    }

    .confirm-footer {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;

      .button {
        padding: 0.75rem 1.5rem;
        border-radius: 4px;
        font-weight: 500;
        cursor: pointer;

        &.cancel-button {
          background-color: transparent;
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: #fff;

          &:hover {
            border-color: rgba(255, 255, 255, 0.5);
          }
        }

        &.is-primary {
          background-color: #1F6FF4;
          border: none;
          color: #fff;

          &:hover {
            background-color: #1557c7;
          }
        }
      }
    }
  }
}
</style>
